import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { SouscriptionPaiement } from '../components/invitation/SouscriptionPaiement';
import { RcdsProduitSelector, PRODUITS } from '../components/invitation/RcdsProduitSelector';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Upload, CheckCircle2, FileText, Search, Calculator, PhoneCall, CreditCard, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UploadRow = ({ id, label, file, onChange }) => (
  <div className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-3">
    <div className="flex items-center gap-2 text-sm text-slate-700">
      <FileText className="h-4 w-4 text-violet-500" /> {label}
    </div>
    <label className="cursor-pointer text-sm text-violet-700 hover:underline flex items-center gap-1">
      <Upload className="h-4 w-4" /> {file ? file.name.slice(0, 22) : 'Choisir'}
      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" data-testid={`upload-${id}`}
        onChange={(e) => onChange(e.target.files?.[0] || null)} />
    </label>
  </div>
);

export default function QuestionnaireMembre() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    raison_sociale: '', siret: '', nom: '', prenom: '', telephone: '',
    adresse: '', code_postal: '', ville: '', territoire: '', ca_ht: '',
  });
  const [files, setFiles] = useState({ kbis: null, piece_identite: null, bilan: null });

  // Activités (sélection comme à la création d'une fiche membre)
  const [activites, setActivites] = useState([]);
  const [selectedActivites, setSelectedActivites] = useState([]);
  const [activiteSearch, setActiviteSearch] = useState('');

  // Simulateur RCDS
  const [sim, setSim] = useState({ montant_ht: '', effectif: '', masse_salariale: '', anciennete: '' });
  const [produit, setProduit] = useState('');
  const [simTermine, setSimTermine] = useState(false);
  const [simActive, setSimActive] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [souscription, setSouscription] = useState(null);
  const [paymentScreen, setPaymentScreen] = useState(null); // {status}
  const [verifying, setVerifying] = useState(false);
  const [souscrireOnly, setSouscrireOnly] = useState(false);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const paiement = params.get('paiement');
      const souscrire = params.get('souscrire');
      // Retour depuis Stripe : on vérifie le paiement au lieu de charger le questionnaire
      if (paiement === 'cancel' || params.get('paiement_annule')) {
        setPaymentScreen({ ok: false, text: 'Paiement annulé : vous pouvez réessayer quand vous le souhaitez.' });
        setLoading(false);
        return;
      }
      if (sessionId) {
        setVerifying(true);
        setLoading(false);
        const isFtpe = params.get('type') === 'ftpe';
        try {
          const url = isFtpe ? `${API}/member-invitation/${token}/ftpe/payment-status/${sessionId}` : `${API}/member-invitation/${token}/souscription/payment-status/${sessionId}`;
          const { data } = await axios.get(url);
          const paid = data.payment_status === 'paid';
          const montant = data.montant ?? data.montant_ttc;
          setPaymentScreen(paid
            ? { ok: true, text: isFtpe ? `Adhésion FTPE réglée (${montant} €). Vous pouvez maintenant régler votre contribution RCDS.` : `Paiement de ${montant} € reçu. Merci !` }
            : { ok: false, text: 'Paiement en cours de validation : actualisez la page dans quelques instants.' });
        } catch {
          setPaymentScreen({ ok: false, text: 'Vérification du paiement impossible pour le moment.' });
        } finally {
          setVerifying(false);
        }
        return;
      }
      // Reprise de souscription depuis la fiche membre : écran de paiement autonome
      if (souscrire === '1') {
        try {
          const { data } = await axios.get(`${API}/member-invitation/${token}/souscription`);
          setSouscription(data);
          setSouscrireOnly(true);
        } catch {
          setInvalid(true);
        } finally {
          setLoading(false);
        }
        return;
      }
      try {
        const res = await axios.get(`${API}/member-invitation/${token}`);
        setForm((p) => ({
          ...p,
          raison_sociale: res.data.raison_sociale || '',
          nom: res.data.nom || '', prenom: res.data.prenom || '',
        }));
        setSimActive(!!res.data.simulation_active);
        try {
          const act = await axios.get(`${API}/member-invitation/${token}/activites`);
          setActivites(Array.isArray(act.data.activites) ? act.data.activites : []);
        } catch { /* activités optionnelles */ }
      } catch {
        setInvalid(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setSimField = (k) => (e) => { setSim((p) => ({ ...p, [k]: e.target.value })); setSimTermine(false); setSimResult(null); };

  const toggleActivite = (a) => {
    setSimTermine(false);
    setSelectedActivites((prev) =>
      prev.some((x) => x.id === a.id) ? prev.filter((x) => x.id !== a.id) : [...prev, { id: a.id, nom: a.nom, categorie: a.categorie }]
    );
  };

  const filteredActivites = activites.filter((a) =>
    !activiteSearch || (a.nom || '').toLowerCase().includes(activiteSearch.toLowerCase())
  );

  // Invité par un COOPER'S : pas de calcul, seul « Terminé » est disponible.
  const handleTermine = () => {
    if (selectedActivites.length === 0) { toast.error('Sélectionnez au moins une activité'); return; }
    setSimTermine(true);
    toast.success('Simulateur enregistré');
  };

  // Invité par le super admin : calcul chiffré via le barème réel.
  const handleCalculer = async () => {
    if (selectedActivites.length === 0) { toast.error('Sélectionnez au moins une activité'); return; }
    if (!produit) { toast.error('Choisissez un simulateur'); return; }
    if (!sim.montant_ht || Number(sim.montant_ht) <= 0) { toast.error(`Indiquez : ${PRODUITS.find((p) => p.id === produit).base}`); return; }
    setCalculating(true);
    try {
      const { data } = await axios.post(`${API}/member-invitation/${token}/simulate-rcds`, { montant_ht: Number(sim.montant_ht), produit });
      setSimResult(data);
      setSimTermine(true);
      toast.success('Estimation calculée');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors du calcul');
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.raison_sociale) { toast.error('La raison sociale est requise'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ''));
      fd.append('activites', JSON.stringify(selectedActivites));
      fd.append('simulateur_rcds', JSON.stringify({
        ca_ht: form.ca_ht,
        produit,
        montant_ht: sim.montant_ht,
        effectif: sim.effectif,
        masse_salariale: sim.masse_salariale,
        anciennete: sim.anciennete,
        termine: simTermine,
        estimation: simResult,
      }));
      Object.entries(files).forEach(([k, f]) => { if (f) fd.append(k, f); });
      const res = await axios.post(`${API}/member-invitation/${token}/submit`, fd);
      setSouscription(res.data?.souscription || null);
      setDone(true);
      toast.success('Dossier transmis avec succès');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || verifying) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>;

  if (paymentScreen || souscrireOnly) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-violet-50 to-white">
      <Card className="max-w-md w-full"><CardContent className="py-8 space-y-4" data-testid="questionnaire-payment-result">
        <h2 className="text-xl font-semibold text-slate-800 text-center flex items-center justify-center gap-2"><CreditCard className="h-5 w-5 text-violet-600" /> Finaliser votre souscription</h2>
        <SouscriptionPaiement token={token} message={paymentScreen} />
      </CardContent></Card>
    </div>
  );

  if (invalid) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md"><CardContent className="py-8 text-center text-slate-600" data-testid="questionnaire-invalid">
        Ce lien d'invitation est invalide ou a expiré.
      </CardContent></Card>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-violet-50 to-white">
      <Card className="max-w-md"><CardContent className="py-10 text-center space-y-4" data-testid="questionnaire-done">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
        <h2 className="text-xl font-semibold text-slate-800">Merci !</h2>
        <p className="text-slate-600">Votre dossier a bien été transmis. <strong>Un COOPER'S va vous rappeler</strong> pour finaliser votre contribution RCDS.</p>
        {souscription && souscription.montant_ttc > 0 && (
          <div className="pt-2 space-y-3 border-t border-slate-100" data-testid="questionnaire-souscription-cb">
            <p className="text-sm text-slate-700">Souscrivez dès maintenant :</p>
            <SouscriptionPaiement token={token} />
            <p className="text-[12px] text-slate-500 flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> Ou plus tard — votre souscription reste dans votre dossier.</p>
          </div>
        )}
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-white py-10 px-4">
      <Card className="max-w-2xl mx-auto" data-testid="questionnaire-form">
        <CardHeader>
          <CardTitle className="text-2xl text-violet-800">Votre dossier membre — O'SCOP Outremer</CardTitle>
          <p className="text-sm text-slate-500">Complétez votre profil, sélectionnez vos activités, remplissez le simulateur RCDS et joignez vos pièces.</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Raison sociale *</Label><Input value={form.raison_sociale} onChange={setField('raison_sociale')} data-testid="q-raison-sociale" /></div>
            <div><Label>SIRET</Label><Input value={form.siret} onChange={setField('siret')} data-testid="q-siret" /></div>
            <div><Label>Nom</Label><Input value={form.nom} onChange={setField('nom')} data-testid="q-nom" /></div>
            <div><Label>Prénom</Label><Input value={form.prenom} onChange={setField('prenom')} data-testid="q-prenom" /></div>
            <div><Label>Téléphone</Label><Input value={form.telephone} onChange={setField('telephone')} data-testid="q-telephone" /></div>
            <div><Label>Territoire</Label><Input value={form.territoire} onChange={setField('territoire')} placeholder="guadeloupe, martinique…" data-testid="q-territoire" /></div>
            <div className="sm:col-span-2"><Label>Adresse</Label><Input value={form.adresse} onChange={setField('adresse')} data-testid="q-adresse" /></div>
            <div><Label>Code postal</Label><Input value={form.code_postal} onChange={setField('code_postal')} data-testid="q-cp" /></div>
            <div><Label>Ville</Label><Input value={form.ville} onChange={setField('ville')} data-testid="q-ville" /></div>
          </div>

          {/* Sélection des activités */}
          <div className="space-y-2" data-testid="q-activites-section">
            <h4 className="text-sm font-semibold text-slate-700">Vos activités {selectedActivites.length > 0 && <span className="text-violet-600">({selectedActivites.length} sélectionnée{selectedActivites.length > 1 ? 's' : ''})</span>}</h4>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={activiteSearch} onChange={(e) => setActiviteSearch(e.target.value)} placeholder="Rechercher une activité…" className="pl-9" data-testid="q-activite-search" />
            </div>
            {selectedActivites.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedActivites.map((a) => (
                  <button key={a.id} type="button" onClick={() => toggleActivite(a)} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-violet-100 text-violet-700 border border-violet-300" data-testid="q-activite-chip">
                    {a.nom} ✕
                  </button>
                ))}
              </div>
            )}
            <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="q-activites-list">
              {filteredActivites.slice(0, 60).map((a) => {
                const on = selectedActivites.some((x) => x.id === a.id);
                return (
                  <button key={a.id} type="button" onClick={() => toggleActivite(a)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${on ? 'bg-violet-50' : ''}`}>
                    <span>{a.nom} <span className="text-xs text-slate-400">· {a.categorie}</span></span>
                    {on && <CheckCircle2 className="h-4 w-4 text-violet-600" />}
                  </button>
                );
              })}
              {filteredActivites.length === 0 && <p className="px-3 py-3 text-sm text-slate-400">Aucune activité.</p>}
            </div>
          </div>

          {/* Simulateur RCDS */}
          <div className="space-y-3 rounded-lg border border-violet-200 bg-violet-50/50 p-4" data-testid="q-simulateur-rcds">
            <h4 className="text-sm font-semibold text-violet-800 flex items-center gap-2"><Calculator className="h-4 w-4" /> Simulateur RCDS</h4>
            <RcdsProduitSelector
              value={produit}
              onChange={(id) => { setProduit(id); setSimResult(null); setSimTermine(false); if (id === 'annuel' && !sim.montant_ht && form.ca_ht) setSim((p) => ({ ...p, montant_ht: form.ca_ht })); }}
              onReset={() => { setProduit(''); setSim({ montant_ht: '', effectif: '', masse_salariale: '', anciennete: '' }); setSimResult(null); setSimTermine(false); }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Chiffre d'affaires HT annuel (€)</Label><Input type="number" min="0" value={form.ca_ht} onChange={(e) => { setField('ca_ht')(e); setSimTermine(false); }} placeholder="ex : 180000" data-testid="q-ca-ht" /></div>
              {simActive && produit && (
                <div><Label>{PRODUITS.find((p) => p.id === produit).base}</Label><Input type="number" min="0" value={sim.montant_ht} onChange={setSimField('montant_ht')} placeholder="ex : 20000" data-testid="q-montant-ht" /></div>
              )}
              <div><Label>Effectif</Label><Input type="number" min="0" value={sim.effectif} onChange={setSimField('effectif')} placeholder="ex : 5" data-testid="q-effectif" /></div>
              <div><Label>Masse salariale (€)</Label><Input type="number" min="0" value={sim.masse_salariale} onChange={setSimField('masse_salariale')} placeholder="ex : 120000" data-testid="q-masse-salariale" /></div>
              <div><Label>Ancienneté (années)</Label><Input type="number" min="0" value={sim.anciennete} onChange={setSimField('anciennete')} placeholder="ex : 3" data-testid="q-anciennete" /></div>
            </div>
            {simActive ? (
              <Button type="button" onClick={handleCalculer} disabled={calculating} variant="outline" className="border-violet-400 text-violet-700" data-testid="q-calculer-btn">
                {calculating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />} Calculer ma contribution
              </Button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" disabled variant="outline" className="opacity-50 cursor-not-allowed" data-testid="q-calculer-btn-disabled" title="Un COOPER'S calculera votre contribution">
                  <Calculator className="h-4 w-4 mr-2" /> Calculer ma contribution
                </Button>
                <Button type="button" onClick={handleTermine} className="bg-violet-600 text-white" data-testid="q-termine-btn">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Terminé
                </Button>
              </div>
            )}
            {simActive && simResult && (
              <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 text-sm" data-testid="q-sim-result">
                <p className="font-semibold text-violet-800 mb-1" data-testid="q-sim-produit">{simResult.produit_label}</p>
                <div className="grid grid-cols-2 gap-1 text-slate-700">
                  <span>{simResult.base_label}</span><span className="text-right font-medium">{simResult.montant_ht} €</span>
                  <span>{simResult.detail}</span><span className="text-right font-medium">{simResult.total_brut} €</span>
                  {simResult.remise > 0 && <><span>Remise PIB ({simResult.remise_pct} %)</span><span className="text-right font-medium">− {simResult.remise} €</span></>}
                  <span className="font-semibold text-violet-800">Contribution à régler</span><span className="text-right font-bold text-violet-800" data-testid="q-sim-total-ttc">{simResult.total_ttc} €</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">{simResult.eligible_4x ? 'Paiement par carte en une fois ou en 4 fois (acompte 60 % puis 3 échéances).' : 'Paiement par carte en une fois.'} Après transmission de votre dossier : adhésion FTPE (200 €) puis contribution.</p>
              </div>
            )}
            {simTermine && (
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="q-sim-message">
                <PhoneCall className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-600 text-white mr-2" data-testid="q-sim-termine-badge">Terminé</span>
                  Un COOPER'S va vous rappeler pour finaliser votre contribution RCDS.
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-700">Pièces justificatives</h4>
            <UploadRow id="kbis" label="Extrait Kbis" file={files.kbis} onChange={(f) => setFiles((p) => ({ ...p, kbis: f }))} />
            <UploadRow id="piece_identite" label="Pièce d'identité" file={files.piece_identite} onChange={(f) => setFiles((p) => ({ ...p, piece_identite: f }))} />
            <UploadRow id="bilan" label="Dernier bilan" file={files.bilan} onChange={(f) => setFiles((p) => ({ ...p, bilan: f }))} />
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white" data-testid="questionnaire-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Transmettre mon dossier
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
